import { cloneEntity } from 'services/utils';


class AlertsAndRulesService {
    /**
     * @param $q
     * @param {RulesApiService} RulesApiService
     */
    constructor($q, RulesApiService, ProjectManager) {
        this.$q = $q;
        this.RulesApiService = RulesApiService;
        this.ProjectManager = ProjectManager;
    }

    listActiveAlerts(pageToken = null) {
        const params = {
            organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
            projects: [`projects/${this.ProjectManager.currentProjectId}`],
            statuses: ['RESOLVED', 'ARCHIVED'],
            includeEvents: true,
            statusesInverted: true,
            pageSize: 10,
        };
        if (pageToken) {
            params.pageToken = pageToken;
        }
        return this.RulesApiService.alerts(params);
    }


    listActiveOrganizationAlerts(params) {
        // Fetch all alerts for the organization
        const alerts = [];
        const fetchAlerts = (nextPageToken = null) => {
            return this.RulesApiService.alerts({
                organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
                statuses: ['RESOLVED', 'ARCHIVED'],
                statusesInverted: true,
                includeEvents: true,
                pageToken: nextPageToken,
                ...params
            }).then(response => {
                alerts.push(...response.data);
                if (response.nextPageToken) {
                    return fetchAlerts(response.nextPageToken);
                }
                return alerts;
            });
        };
        return fetchAlerts();
    }

    listAllOrganizationAlerts(params) {
        // Fetch all alerts for the organization
        const alerts = [];
        const fetchAlerts = (nextPageToken = null) => {
            return this.RulesApiService.alerts({
                organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
                includeEvents: true,
                pageToken: nextPageToken,
                ...params
            }).then(response => {
                alerts.push(...response.data);
                if (response.nextPageToken) {
                    return fetchAlerts(response.nextPageToken);
                }
                return alerts;
            });
        };
        return fetchAlerts();
    }

    listAlerts(params) {
        return this.RulesApiService.alerts(
            {
                organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
                ...params
            }
        )
    }

    listAggregatedAlerts(params) {
        return this.RulesApiService.aggregatedAlerts({
            organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
            ...params
        });
    }

    acknowledgeAlert(alert, comment = '') {
        return this.RulesApiService.acknowledgeAlert(alert, comment);
    }

    addComment(alert, comment) {
        return this.RulesApiService.addComment(alert, comment);
    }

    addCorrectiveAction(alert, correctiveAction) {
        return this.RulesApiService.addCorrectiveAction(alert, correctiveAction);
    }

    updateAlertTags(alert, tags) {
        const normalizedTags = Array.isArray(tags)
            ? tags
                .map(tag => (tag || '').trim())
                .filter(tag => tag.length > 0)
                .filter((tag, index, self) => self.indexOf(tag) === index)
            : [];
        return this.RulesApiService.updateAlert(alert, { tags: normalizedTags }, 'tags');
    }

    listActiveAlertsForDevice(deviceResourceName) {
        // Extract project ID from device resource name (e.g., "projects/abc/devices/xyz" -> "abc")
        const projectId = deviceResourceName.split('/')[1];

        if (!projectId) {
            // If we can't extract project ID, return empty result
            return Promise.resolve({ data: [] });
        }

        // Get all active alerts for the specific project, then filter by device
        return this.RulesApiService.alerts({
            organization: `organizations/${this.ProjectManager.currentProject.organizationId}`,
            projects: [`projects/${projectId}`], // Can't use ProjectManager.currentProjectId since we could be viewing the org alerts page
            statuses: ['RESOLVED', 'ARCHIVED'],
            statusesInverted: true,
            includeEvents: false
        }).then(response => {
            // Filter alerts to only include those for the specified device
            const filteredAlerts = (response.data || []).filter(alert =>
                alert.device === deviceResourceName
            );

            // Return just the filtered alerts
            return { data: filteredAlerts };
        });
    }

    archiveAlert(alert, comment = '') {
        return this.RulesApiService.archiveAlert(alert, comment);
    }

    getAlert(alertName) {
        return this.RulesApiService.getAlert(alertName);
    }

    /**
     * @param {string} id
     * @return {Rule}
     */
    getRule(id) {
        return this.RulesApiService.rule(id);
    }

    /**
     * Create (when id is missing) or update rule.
     * @param {Rule} originalRule
     */
    saveRule(originalRule) {
        const rule = cloneEntity(originalRule);
        if (!rule.name) {
            return this.RulesApiService.createRule(originalRule).then();
        }
        return this.RulesApiService.updateRule(rule);
    }

    /**
     * @param {string} id
     */
    deleteRule(id) {
        return this.RulesApiService.deleteRule(id);
    }

    /**
     * @return {Rule[]}
     */
    listRules() {
        return this.RulesApiService.rules();
    }

    /**
     * @return {DeviceTriggerStatus[]}
     */
    listTriggerStatus(id) {
        return this.RulesApiService.listTriggerStatus(id);
    }

    /**
     *
     * @param {Rule} rule
     * @param {Device} device
     * @param {DeviceEvent | undefined} deviceEvent
     * @return {boolean | undefined}
     */
    evaluateRule(rule, device, deviceEvent=undefined) { // eslint-disable-line class-methods-use-this
        switch (rule?.trigger?.field) {
            case 'touchCount': // Fallthrough
            case 'touch':
                if (deviceEvent?.eventType === 'touch') {
                    return true
                }
                return undefined;
            case 'temperature': {
                const temperature = device?.reported?.temperature?.value;
                if (temperature === undefined) {
                    return undefined;
                }
                const range = rule.trigger.range;
                let lower = range.lower;
                let upper = range.upper;
                if (range.lower === undefined) {
                    lower = -1e100
                }
                if (range.upper === undefined) {
                    upper = 1e100
                }
                const insideRange = lower <= temperature && temperature <= upper;
                return insideRange && range.type === 'WITHIN' || !insideRange && range.type === 'OUTSIDE';
            }
            case 'proximityCount': // Fallthrough
            case 'objectPresent': {
                const present = device?.reported?.objectPresent?.state;
                if (present === undefined) {
                    return undefined;
                }
                return rule.trigger.presence === present
            }
            case 'contactCount': // Fallthrough
            case 'contact': {
                const contact = device?.reported?.contact?.state;
                if (contact === undefined) {
                    return undefined;
                }
                return rule.trigger.contact === contact
            }
            default:
                return undefined;
        }
    }
}

/**
 * @ngInject
 */
export default function AlertsAndRulesServiceFactory($q, RulesApiService, ProjectManager) {
    return new AlertsAndRulesService($q, RulesApiService, ProjectManager);
}


/**
 * @typedef {Object} RuleEvalResult
 * @property {boolean | undefined} result
 */
